import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart, ComposedChart } from 'recharts';
import { 
  Activity, 
  Users, 
  Building2, 
  MapPin, 
  Calendar, 
  TrendingDown, 
  TrendingUp,
  Stethoscope,
  HeartPulse,
  Pill,
  UtensilsCrossed,
  Croissant,
  Beef,
  Flower2,
  Wrench,
  Hammer,
  PaintBucket,
  Zap,
  Car,
  Home,
  Scissors,
  Camera,
  BookOpen,
  Scale,
  Briefcase,
  Calculator,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Eye,
  Star,
  AlertTriangle,
  Info,
  CheckCircle,
  Target,
  BarChart3,
  PieChart as PieChartIcon,
  Globe,
  Award,
  Clock,
  Percent
} from 'lucide-react';

// Données complètes et détaillées des professionnels avec informations approfondies
const professionalData = {
  sante: [
    { 
      name: 'Médecins généralistes', 
      total: 126000, 
      density: 188.1, 
      avgAge: 52.1, 
      departures: 41580, 
      rate: 33.0, 
      icon: Stethoscope, 
      category: 'Santé',
      description: 'Médecins de premier recours assurant les soins de base et la coordination des parcours de soins',
      formation: 'Doctorat en médecine (9 ans) + DES médecine générale (3 ans)',
      salaire: '85000-120000',
      evolution: '+2.1%',
      tension: 'Forte',
      specialites: ['Médecine familiale', 'Gériatrie', 'Pédiatrie', 'Urgences'],
      regions_tension: ['Déserts médicaux ruraux', 'Banlieues défavorisées']
    },
    { 
      name: 'Médecins spécialistes', 
      total: 100000, 
      density: 149.4, 
      avgAge: 50.8, 
      departures: 33000, 
      rate: 33.0, 
      icon: Stethoscope, 
      category: 'Santé',
      description: 'Médecins experts dans une discipline médicale spécifique',
      formation: 'Doctorat en médecine (9 ans) + DES spécialisé (4-5 ans)',
      salaire: '120000-300000',
      evolution: '+1.8%',
      tension: 'Très forte',
      specialites: ['Cardiologie', 'Oncologie', 'Neurologie', 'Chirurgie', 'Radiologie'],
      regions_tension: ['Toutes régions hors grandes métropoles']
    },
    { 
      name: 'Infirmiers diplômés d\'État', 
      total: 764000, 
      density: 1140.5, 
      avgAge: 43.8, 
      departures: 185000, 
      rate: 24.2, 
      icon: HeartPulse, 
      category: 'Santé',
      description: 'Professionnels de santé assurant les soins infirmiers et l\'accompagnement des patients',
      formation: 'Diplôme d\'État infirmier (3 ans)',
      salaire: '28000-45000',
      evolution: '+3.2%',
      tension: 'Modérée',
      specialites: ['Soins généraux', 'Réanimation', 'Bloc opératoire', 'Psychiatrie', 'Pédiatrie'],
      regions_tension: ['EHPAD', 'Hôpitaux publics']
    },
    { 
      name: 'Pharmaciens d\'officine', 
      total: 74500, 
      density: 111.2, 
      avgAge: 48.5, 
      departures: 18500, 
      rate: 24.8, 
      icon: Pill, 
      category: 'Santé',
      description: 'Professionnels du médicament assurant la dispensation et le conseil pharmaceutique',
      formation: 'Doctorat en pharmacie (6 ans)',
      salaire: '45000-80000',
      evolution: '+0.8%',
      tension: 'Faible',
      specialites: ['Officine', 'Hospitalier', 'Industrie', 'Biologie médicale'],
      regions_tension: ['Zones rurales isolées']
    },
    { 
      name: 'Chirurgiens-dentistes', 
      total: 42000, 
      density: 62.7, 
      avgAge: 49.2, 
      departures: 12600, 
      rate: 30.0, 
      icon: Stethoscope, 
      category: 'Santé',
      description: 'Spécialistes de la santé bucco-dentaire et de la chirurgie orale',
      formation: 'Doctorat en chirurgie dentaire (6 ans)',
      salaire: '65000-150000',
      evolution: '+1.2%',
      tension: 'Modérée',
      specialites: ['Dentisterie générale', 'Orthodontie', 'Chirurgie orale', 'Parodontologie'],
      regions_tension: ['Zones rurales', 'Quartiers défavorisés']
    },
    { 
      name: 'Masseurs-kinésithérapeutes', 
      total: 95000, 
      density: 141.8, 
      avgAge: 41.5, 
      departures: 19000, 
      rate: 20.0, 
      icon: HeartPulse, 
      category: 'Santé',
      description: 'Professionnels de la rééducation fonctionnelle et de la thérapie manuelle',
      formation: 'Diplôme d\'État de masseur-kinésithérapeute (4 ans)',
      salaire: '35000-65000',
      evolution: '+4.1%',
      tension: 'Faible',
      specialites: ['Rééducation orthopédique', 'Neurologie', 'Respiratoire', 'Sport'],
      regions_tension: ['Demande croissante partout']
    },
    { 
      name: 'Orthophonistes', 
      total: 25000, 
      density: 37.3, 
      avgAge: 38.2, 
      departures: 3750, 
      rate: 15.0, 
      icon: HeartPulse, 
      category: 'Santé',
      description: 'Spécialistes de la rééducation des troubles de la communication',
      formation: 'Master en orthophonie (5 ans)',
      salaire: '32000-55000',
      evolution: '+5.2%',
      tension: 'Forte',
      specialites: ['Troubles du langage', 'Déglutition', 'Voix', 'Neurologie'],
      regions_tension: ['Toutes régions - forte demande']
    },
    { 
      name: 'Psychologues cliniciens', 
      total: 48000, 
      density: 71.6, 
      avgAge: 42.8, 
      departures: 7200, 
      rate: 15.0, 
      icon: HeartPulse, 
      category: 'Santé',
      description: 'Professionnels de la santé mentale et du soutien psychologique',
      formation: 'Master en psychologie (5 ans)',
      salaire: '30000-60000',
      evolution: '+6.8%',
      tension: 'Très forte',
      specialites: ['Psychologie clinique', 'Neuropsychologie', 'Psychologie du travail'],
      regions_tension: ['Secteur public - forte demande']
    },
    { 
      name: 'Sages-femmes', 
      total: 23000, 
      density: 34.3, 
      avgAge: 40.5, 
      departures: 3450, 
      rate: 15.0, 
      icon: HeartPulse, 
      category: 'Santé',
      description: 'Professionnelles de la périnatalité et de la santé génésique',
      formation: 'Diplôme d\'État de sage-femme (5 ans)',
      salaire: '35000-55000',
      evolution: '+2.8%',
      tension: 'Modérée',
      specialites: ['Obstétrique', 'Gynécologie', 'Contraception', 'IVG'],
      regions_tension: ['Maternités de proximité']
    },
    { 
      name: 'Opticiens-lunetiers', 
      total: 15000, 
      density: 22.4, 
      avgAge: 44.2, 
      departures: 3000, 
      rate: 20.0, 
      icon: Eye, 
      category: 'Santé',
      description: 'Professionnels de l\'optique et de l\'adaptation des équipements visuels',
      formation: 'BTS Opticien-lunetier (2 ans)',
      salaire: '25000-45000',
      evolution: '+1.5%',
      tension: 'Faible',
      specialites: ['Optique de ville', 'Contactologie', 'Basse vision'],
      regions_tension: ['Zones rurales']
    }
  ],
  artisans: [
    { 
      name: 'Boulangers-pâtissiers', 
      total: 57200, 
      density: 85.3, 
      avgAge: 45.5, 
      departures: 14700, 
      rate: 25.7, 
      icon: Croissant, 
      category: 'Artisanat',
      description: 'Artisans de la panification et de la pâtisserie traditionnelle française',
      formation: 'CAP Boulanger + CAP Pâtissier (2-3 ans)',
      salaire: '22000-45000',
      evolution: '-1.2%',
      tension: 'Forte',
      specialites: ['Boulangerie traditionnelle', 'Pâtisserie fine', 'Viennoiserie', 'Sans gluten'],
      regions_tension: ['Centres-villes', 'Zones rurales']
    },
    { 
      name: 'Bouchers-charcutiers', 
      total: 47000, 
      density: 70.1, 
      avgAge: 47.8, 
      departures: 14100, 
      rate: 30.0, 
      icon: Beef, 
      category: 'Artisanat',
      description: 'Artisans de la transformation et commercialisation des viandes',
      formation: 'CAP Boucher + CAP Charcutier-traiteur (2-3 ans)',
      salaire: '24000-50000',
      evolution: '-2.1%',
      tension: 'Très forte',
      specialites: ['Boucherie traditionnelle', 'Charcuterie', 'Traiteur', 'Halal/Casher'],
      regions_tension: ['Toutes régions - métier en tension']
    },
    { 
      name: 'Électriciens du bâtiment', 
      total: 125000, 
      density: 186.6, 
      avgAge: 44.8, 
      departures: 25000, 
      rate: 20.0, 
      icon: Zap, 
      category: 'Artisanat',
      description: 'Professionnels de l\'installation et maintenance électrique',
      formation: 'CAP Électricien + mentions complémentaires (2-3 ans)',
      salaire: '28000-55000',
      evolution: '+3.8%',
      tension: 'Forte',
      specialites: ['Résidentiel', 'Tertiaire', 'Industriel', 'Domotique', 'Énergies renouvelables'],
      regions_tension: ['Forte demande partout - transition énergétique']
    },
    { 
      name: 'Plombiers-chauffagistes', 
      total: 85000, 
      density: 126.9, 
      avgAge: 45.2, 
      departures: 21250, 
      rate: 25.0, 
      icon: Wrench, 
      category: 'Artisanat',
      description: 'Spécialistes des installations sanitaires et thermiques',
      formation: 'CAP Installateur sanitaire + CAP Installateur thermique (2-3 ans)',
      salaire: '26000-52000',
      evolution: '+2.9%',
      tension: 'Forte',
      specialites: ['Plomberie sanitaire', 'Chauffage', 'Climatisation', 'Énergies renouvelables'],
      regions_tension: ['Rénovation énergétique - forte demande']
    },
    { 
      name: 'Menuisiers-ébénistes', 
      total: 68000, 
      density: 101.5, 
      avgAge: 46.5, 
      departures: 17000, 
      rate: 25.0, 
      icon: Hammer, 
      category: 'Artisanat',
      description: 'Artisans du travail du bois et de l\'aménagement',
      formation: 'CAP Menuisier + spécialisations (2-4 ans)',
      salaire: '24000-48000',
      evolution: '+1.1%',
      tension: 'Modérée',
      specialites: ['Menuiserie d\'agencement', 'Ébénisterie', 'Charpente', 'Restauration'],
      regions_tension: ['Agencement haut de gamme']
    },
    { 
      name: 'Maçons-coffreurs', 
      total: 95000, 
      density: 141.8, 
      avgAge: 44.2, 
      departures: 19000, 
      rate: 20.0, 
      icon: Hammer, 
      category: 'Artisanat',
      description: 'Professionnels du gros œuvre et de la construction',
      formation: 'CAP Maçon + spécialisations (2-3 ans)',
      salaire: '25000-45000',
      evolution: '+2.3%',
      tension: 'Forte',
      specialites: ['Gros œuvre', 'Béton armé', 'Rénovation', 'Éco-construction'],
      regions_tension: ['Construction neuve et rénovation']
    },
    { 
      name: 'Peintres en bâtiment', 
      total: 78000, 
      density: 116.4, 
      avgAge: 45.5, 
      departures: 19500, 
      rate: 25.0, 
      icon: PaintBucket, 
      category: 'Artisanat',
      description: 'Spécialistes de la finition et décoration des bâtiments',
      formation: 'CAP Peintre-applicateur de revêtements (2 ans)',
      salaire: '23000-42000',
      evolution: '+1.8%',
      tension: 'Modérée',
      specialites: ['Peinture décorative', 'Ravalement', 'Papier peint', 'Finitions haut de gamme'],
      regions_tension: ['Rénovation urbaine']
    },
    { 
      name: 'Mécaniciens automobiles', 
      total: 155000, 
      density: 231.3, 
      avgAge: 43.5, 
      departures: 31000, 
      rate: 20.0, 
      icon: Car, 
      category: 'Artisanat',
      description: 'Techniciens de la réparation et maintenance automobile',
      formation: 'CAP Maintenance véhicules + spécialisations (2-4 ans)',
      salaire: '26000-48000',
      evolution: '+0.8%',
      tension: 'Forte',
      specialites: ['Mécanique générale', 'Électronique embarquée', 'Véhicules électriques', 'Diagnostic'],
      regions_tension: ['Transition vers l\'électrique - reconversion nécessaire']
    },
    { 
      name: 'Coiffeurs-stylistes', 
      total: 185000, 
      density: 276.1, 
      avgAge: 41.2, 
      departures: 27750, 
      rate: 15.0, 
      icon: Scissors, 
      category: 'Artisanat',
      description: 'Professionnels de la coiffure et de l\'esthétique capillaire',
      formation: 'CAP Métiers de la coiffure + BP (2-3 ans)',
      salaire: '20000-35000',
      evolution: '+1.5%',
      tension: 'Faible',
      specialites: ['Coiffure mixte', 'Coloriste', 'Extensions', 'Coiffure événementielle'],
      regions_tension: ['Salons haut de gamme']
    },
    { 
      name: 'Carreleurs-mosaïstes', 
      total: 28000, 
      density: 41.8, 
      avgAge: 45.2, 
      departures: 7000, 
      rate: 25.0, 
      icon: Hammer, 
      category: 'Artisanat',
      description: 'Spécialistes de la pose de revêtements céramiques',
      formation: 'CAP Carreleur mosaïste (2 ans)',
      salaire: '25000-45000',
      evolution: '+2.1%',
      tension: 'Forte',
      specialites: ['Carrelage traditionnel', 'Faïence', 'Mosaïque d\'art', 'Sols techniques'],
      regions_tension: ['Rénovation salle de bains - forte demande']
    }
  ],
  commercants: [
    { 
      name: 'Restaurateurs traditionnels', 
      total: 175000, 
      density: 261.2, 
      avgAge: 47.2, 
      departures: 42000, 
      rate: 24.0, 
      icon: UtensilsCrossed, 
      category: 'Commerce',
      description: 'Professionnels de la restauration et de la gastronomie française',
      formation: 'CAP Cuisine + CAP Restaurant ou formations spécialisées',
      salaire: '25000-80000',
      evolution: '-0.8%',
      tension: 'Très forte',
      specialites: ['Cuisine traditionnelle', 'Gastronomie', 'Restauration rapide', 'Traiteur'],
      regions_tension: ['Difficultés de recrutement généralisées']
    },
    { 
      name: 'Hôteliers-restaurateurs', 
      total: 45000, 
      density: 67.2, 
      avgAge: 49.8, 
      departures: 13500, 
      rate: 30.0, 
      icon: Building2, 
      category: 'Commerce',
      description: 'Gestionnaires d\'établissements d\'hébergement touristique',
      formation: 'BTS Hôtellerie-restauration ou formations spécialisées',
      salaire: '30000-100000',
      evolution: '+1.2%',
      tension: 'Forte',
      specialites: ['Hôtellerie de luxe', 'Hôtellerie familiale', 'Chambres d\'hôtes', 'Restauration'],
      regions_tension: ['Zones touristiques - saisonnalité']
    },
    { 
      name: 'Fleuristes-décorateurs', 
      total: 14800, 
      density: 22.1, 
      avgAge: 44.5, 
      departures: 3800, 
      rate: 25.7, 
      icon: Flower2, 
      category: 'Commerce',
      description: 'Artistes floraux et décorateurs événementiels',
      formation: 'CAP Fleuriste + formations artistiques',
      salaire: '20000-40000',
      evolution: '-1.5%',
      tension: 'Modérée',
      specialites: ['Art floral', 'Décoration événementielle', 'Plantes d\'intérieur', 'Funéraire'],
      regions_tension: ['Concurrence des grandes surfaces']
    },
    { 
      name: 'Libraires indépendants', 
      total: 8500, 
      density: 12.7, 
      avgAge: 46.2, 
      departures: 1700, 
      rate: 20.0, 
      icon: BookOpen, 
      category: 'Commerce',
      description: 'Professionnels du livre et de la culture littéraire',
      formation: 'Formation littéraire + gestion commerciale',
      salaire: '22000-45000',
      evolution: '-2.8%',
      tension: 'Forte',
      specialites: ['Littérature générale', 'Jeunesse', 'BD/Manga', 'Livres spécialisés'],
      regions_tension: ['Concurrence numérique - centres-villes']
    },
    { 
      name: 'Photographes professionnels', 
      total: 25000, 
      density: 37.3, 
      avgAge: 42.8, 
      departures: 3750, 
      rate: 15.0, 
      icon: Camera, 
      category: 'Commerce',
      description: 'Artistes et techniciens de l\'image photographique',
      formation: 'Formations artistiques + techniques photo',
      salaire: '25000-60000',
      evolution: '+2.1%',
      tension: 'Modérée',
      specialites: ['Portrait', 'Mariage', 'Mode', 'Reportage', 'Industriel'],
      regions_tension: ['Évolution vers le numérique']
    },
    { 
      name: 'Épiciers de proximité', 
      total: 65000, 
      density: 97.0, 
      avgAge: 46.8, 
      departures: 16250, 
      rate: 25.0, 
      icon: Building2, 
      category: 'Commerce',
      description: 'Commerçants de l\'alimentation générale de proximité',
      formation: 'Formation commerciale + gestion',
      salaire: '25000-55000',
      evolution: '+0.5%',
      tension: 'Modérée',
      specialites: ['Épicerie fine', 'Produits bio', 'Commerce équitable', 'Produits locaux'],
      regions_tension: ['Concurrence grandes surfaces']
    },
    { 
      name: 'Primeurs-maraîchers', 
      total: 18000, 
      density: 26.9, 
      avgAge: 45.2, 
      departures: 4500, 
      rate: 25.0, 
      icon: Building2, 
      category: 'Commerce',
      description: 'Spécialistes de la vente de fruits et légumes frais',
      formation: 'Formation commerciale + connaissance produits',
      salaire: '23000-45000',
      evolution: '+1.8%',
      tension: 'Faible',
      specialites: ['Fruits et légumes bio', 'Produits de saison', 'Circuits courts'],
      regions_tension: ['Marchés de plein air']
    },
    { 
      name: 'Poissonniers-écaillers', 
      total: 12000, 
      density: 17.9, 
      avgAge: 47.8, 
      departures: 3600, 
      rate: 30.0, 
      icon: Building2, 
      category: 'Commerce',
      description: 'Professionnels de la vente de produits de la mer',
      formation: 'CAP Poissonnier + formations spécialisées',
      salaire: '24000-48000',
      evolution: '-1.2%',
      tension: 'Forte',
      specialites: ['Poissons frais', 'Fruits de mer', 'Sushi', 'Produits fumés'],
      regions_tension: ['Métier physique - difficultés recrutement']
    },
    { 
      name: 'Fromagers-crémiers', 
      total: 8500, 
      density: 12.7, 
      avgAge: 46.5, 
      departures: 2125, 
      rate: 25.0, 
      icon: Building2, 
      category: 'Commerce',
      description: 'Experts en fromages et produits laitiers artisanaux',
      formation: 'Formation spécialisée fromages + commerce',
      salaire: '25000-50000',
      evolution: '+2.5%',
      tension: 'Faible',
      specialites: ['Fromages fermiers', 'Affinage', 'Produits laitiers', 'Conseils dégustation'],
      regions_tension: ['Valorisation terroir français']
    },
    { 
      name: 'Cavistes-œnologues', 
      total: 6500, 
      density: 9.7, 
      avgAge: 44.8, 
      departures: 1300, 
      rate: 20.0, 
      icon: Building2, 
      category: 'Commerce',
      description: 'Spécialistes des vins et spiritueux',
      formation: 'Formation œnologie + commerce spécialisé',
      salaire: '28000-55000',
      evolution: '+1.8%',
      tension: 'Faible',
      specialites: ['Vins français', 'Spiritueux', 'Conseils accords', 'Dégustation'],
      regions_tension: ['Œnotourisme en développement']
    }
  ],
  services: [
    { 
      name: 'Avocats généralistes', 
      total: 72000, 
      density: 107.5, 
      avgAge: 45.8, 
      departures: 14400, 
      rate: 20.0, 
      icon: Scale, 
      category: 'Services',
      description: 'Professionnels du droit et de la défense juridique',
      formation: 'Master en droit + CAPA (5-6 ans)',
      salaire: '45000-150000',
      evolution: '+2.8%',
      tension: 'Modérée',
      specialites: ['Droit civil', 'Droit pénal', 'Droit des affaires', 'Droit social'],
      regions_tension: ['Accès au droit - zones rurales']
    },
    { 
      name: 'Notaires', 
      total: 12500, 
      density: 18.7, 
      avgAge: 48.2, 
      departures: 3125, 
      rate: 25.0, 
      icon: Scale, 
      category: 'Services',
      description: 'Officiers publics et ministériels du droit immobilier',
      formation: 'Master en droit + DSN (6-7 ans)',
      salaire: '60000-200000',
      evolution: '+1.2%',
      tension: 'Faible',
      specialites: ['Immobilier', 'Succession', 'Famille', 'Entreprise'],
      regions_tension: ['Profession réglementée']
    },
    { 
      name: 'Experts-comptables', 
      total: 22000, 
      density: 32.8, 
      avgAge: 46.8, 
      departures: 5500, 
      rate: 25.0, 
      icon: Calculator, 
      category: 'Services',
      description: 'Professionnels de la comptabilité et du conseil aux entreprises',
      formation: 'Master CCA + DEC (8 ans)',
      salaire: '50000-120000',
      evolution: '+2.1%',
      tension: 'Modérée',
      specialites: ['Audit', 'Conseil fiscal', 'Gestion', 'Transmission d\'entreprise'],
      regions_tension: ['Digitalisation du métier']
    },
    { 
      name: 'Conseillers financiers', 
      total: 35000, 
      density: 52.2, 
      avgAge: 44.2, 
      departures: 7000, 
      rate: 20.0, 
      icon: Briefcase, 
      category: 'Services',
      description: 'Spécialistes du conseil en gestion de patrimoine',
      formation: 'Master finance + certifications professionnelles',
      salaire: '40000-100000',
      evolution: '+3.5%',
      tension: 'Faible',
      specialites: ['Gestion de patrimoine', 'Assurance-vie', 'Immobilier', 'Retraite'],
      regions_tension: ['Vieillissement population - forte demande']
    },
    { 
      name: 'Agents immobiliers', 
      total: 45000, 
      density: 67.2, 
      avgAge: 43.8, 
      departures: 6750, 
      rate: 15.0, 
      icon: Home, 
      category: 'Services',
      description: 'Professionnels de la transaction et gestion immobilière',
      formation: 'BTS Professions immobilières + carte professionnelle',
      salaire: '30000-80000',
      evolution: '+2.8%',
      tension: 'Faible',
      specialites: ['Transaction', 'Location', 'Gestion locative', 'Immobilier d\'entreprise'],
      regions_tension: ['Marché immobilier dynamique']
    },
    { 
      name: 'Architectes', 
      total: 32000, 
      density: 47.8, 
      avgAge: 44.5, 
      departures: 4800, 
      rate: 15.0, 
      icon: Home, 
      category: 'Services',
      description: 'Concepteurs et maîtres d\'œuvre des projets architecturaux',
      formation: 'Diplôme d\'architecte HMONP (6 ans)',
      salaire: '35000-80000',
      evolution: '+1.8%',
      tension: 'Modérée',
      specialites: ['Architecture résidentielle', 'Tertiaire', 'Patrimoine', 'Urbanisme'],
      regions_tension: ['Rénovation énergétique - opportunités']
    },
    { 
      name: 'Consultants en management', 
      total: 85000, 
      density: 126.9, 
      avgAge: 42.5, 
      departures: 12750, 
      rate: 15.0, 
      icon: Briefcase, 
      category: 'Services',
      description: 'Experts en stratégie et organisation d\'entreprise',
      formation: 'Master + spécialisations sectorielles',
      salaire: '45000-120000',
      evolution: '+4.2%',
      tension: 'Faible',
      specialites: ['Stratégie', 'Organisation', 'Digital', 'RH', 'Finance'],
      regions_tension: ['Transformation digitale - forte demande']
    },
    { 
      name: 'Formateurs professionnels', 
      total: 65000, 
      density: 97.0, 
      avgAge: 43.2, 
      departures: 9750, 
      rate: 15.0, 
      icon: BookOpen, 
      category: 'Services',
      description: 'Spécialistes de la formation continue et du développement des compétences',
      formation: 'Master + certifications pédagogiques',
      salaire: '32000-65000',
      evolution: '+5.1%',
      tension: 'Faible',
      specialites: ['Formation digitale', 'Management', 'Techniques', 'Soft skills'],
      regions_tension: ['Formation tout au long de la vie - croissance']
    },
    { 
      name: 'Traducteurs-interprètes', 
      total: 12000, 
      density: 17.9, 
      avgAge: 41.8, 
      departures: 1800, 
      rate: 15.0, 
      icon: BookOpen, 
      category: 'Services',
      description: 'Professionnels de la communication multilingue',
      formation: 'Master traduction/interprétation + spécialisations',
      salaire: '28000-60000',
      evolution: '+3.2%',
      tension: 'Modérée',
      specialites: ['Traduction technique', 'Juridique', 'Médicale', 'Littéraire'],
      regions_tension: ['Mondialisation - demande croissante']
    },
    { 
      name: 'Coachs professionnels', 
      total: 18000, 
      density: 26.9, 
      avgAge: 40.5, 
      departures: 1800, 
      rate: 10.0, 
      icon: Users, 
      category: 'Services',
      description: 'Accompagnateurs du développement personnel et professionnel',
      formation: 'Certifications coaching + expérience professionnelle',
      salaire: '35000-80000',
      evolution: '+8.5%',
      tension: 'Faible',
      specialites: ['Coaching exécutif', 'Carrière', 'Équipe', 'Transition'],
      regions_tension: ['Secteur en forte croissance']
    }
  ]
};

// Données régionales détaillées avec informations économiques
const regionalData = [
  { 
    region: 'Île-de-France', 
    density: 580.2, 
    population: 12278000,
    pib: 765000,
    chomage: 7.2,
    tension: 'Très forte',
    secteurs: ['Services', 'Finance', 'Tech', 'Santé']
  },
  { 
    region: 'Provence-Alpes-Côte d\'Azur', 
    density: 487.3, 
    population: 5081000,
    pib: 168000,
    chomage: 8.8,
    tension: 'Forte',
    secteurs: ['Tourisme', 'Santé', 'Artisanat', 'Commerce']
  },
  { 
    region: 'Auvergne-Rhône-Alpes', 
    density: 423.8, 
    population: 8078000,
    pib: 272000,
    chomage: 6.9,
    tension: 'Modérée',
    secteurs: ['Industrie', 'Santé', 'Services', 'Tourisme']
  },
  { 
    region: 'Occitanie', 
    density: 408.5, 
    population: 5924000,
    pib: 178000,
    chomage: 8.1,
    tension: 'Modérée',
    secteurs: ['Aéronautique', 'Santé', 'Agriculture', 'Tourisme']
  },
  { 
    region: 'Nouvelle-Aquitaine', 
    density: 392.1, 
    population: 6010000,
    pib: 180000,
    chomage: 7.5,
    tension: 'Modérée',
    secteurs: ['Agriculture', 'Industrie', 'Santé', 'Tourisme']
  },
  { 
    region: 'Grand Est', 
    density: 352.8, 
    population: 5511000,
    pib: 159000,
    chomage: 8.3,
    tension: 'Forte',
    secteurs: ['Industrie', 'Santé', 'Services', 'Agriculture']
  },
  { 
    region: 'Pays de la Loire', 
    density: 337.2, 
    population: 3801000,
    pib: 119000,
    chomage: 6.2,
    tension: 'Faible',
    secteurs: ['Industrie', 'Agriculture', 'Santé', 'Services']
  },
  { 
    region: 'Bretagne', 
    density: 328.5, 
    population: 3340000,
    pib: 102000,
    chomage: 6.8,
    tension: 'Faible',
    secteurs: ['Agriculture', 'Agroalimentaire', 'Santé', 'Tourisme']
  },
  { 
    region: 'Hauts-de-France', 
    density: 312.4, 
    population: 5978000,
    pib: 161000,
    chomage: 10.1,
    tension: 'Très forte',
    secteurs: ['Industrie', 'Santé', 'Services', 'Agriculture']
  },
  { 
    region: 'Normandie', 
    density: 298.7, 
    population: 3325000,
    pib: 92000,
    chomage: 7.8,
    tension: 'Modérée',
    secteurs: ['Agriculture', 'Industrie', 'Santé', 'Tourisme']
  },
  { 
    region: 'Bourgogne-Franche-Comté', 
    density: 285.3, 
    population: 2783000,
    pib: 79000,
    chomage: 7.1,
    tension: 'Modérée',
    secteurs: ['Agriculture', 'Industrie', 'Santé', 'Tourisme']
  },
  { 
    region: 'Centre-Val de Loire', 
    density: 270.8, 
    population: 2573000,
    pib: 71000,
    chomage: 7.4,
    tension: 'Modérée',
    secteurs: ['Agriculture', 'Industrie', 'Santé', 'Services']
  }
];

// Données d'évolution temporelle
const evolutionData = [
  { year: '2019', total: 2850000, sante: 1180000, artisans: 890000, commercants: 485000, services: 295000 },
  { year: '2020', total: 2820000, sante: 1195000, artisans: 875000, commercants: 465000, services: 285000 },
  { year: '2021', total: 2890000, sante: 1215000, artisans: 885000, commercants: 475000, services: 315000 },
  { year: '2022', total: 2950000, sante: 1235000, artisans: 895000, commercants: 485000, services: 335000 },
  { year: '2023', total: 3020000, sante: 1255000, artisans: 905000, commercants: 495000, services: 365000 },
  { year: '2024', total: 3085000, sante: 1275000, artisans: 915000, commercants: 505000, services: 390000 }
];

// Projections futures
const projectionData = [
  { year: '2024', effectif: 3085000, besoin: 3085000 },
  { year: '2025', effectif: 3120000, besoin: 3150000 },
  { year: '2026', effectif: 3145000, besoin: 3220000 },
  { year: '2027', effectif: 3165000, besoin: 3295000 },
  { year: '2028', effectif: 3180000, besoin: 3375000 },
  { year: '2029', effectif: 3190000, besoin: 3460000 }
];

const COLORS = ['#F97316', '#FB923C', '#FDBA74', '#FED7AA', '#FFF7ED'];

export default function Index() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProfession, setSelectedProfession] = useState('medecins');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedRegion, setSelectedRegion] = useState('all');

  // Données combinées pour l'affichage
  const allProfessionals = useMemo(() => {
    const combined = [
      ...professionalData.sante,
      ...professionalData.artisans,
      ...professionalData.commercants,
      ...professionalData.services
    ];
    
    let filtered = combined;
    
    // Filtrage par catégorie
    if (selectedCategory !== 'all') {
      const categoryMap = {
        'sante': 'Santé',
        'artisans': 'Artisanat',
        'commercants': 'Commerce',
        'services': 'Services'
      };
      filtered = filtered.filter(p => p.category === categoryMap[selectedCategory]);
    }
    
    // Filtrage par recherche
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Tri
    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });
    
    return filtered;
  }, [selectedCategory, searchTerm, sortBy, sortOrder]);

  // Statistiques globales enrichies
  const totalStats = useMemo(() => {
    const total = allProfessionals.reduce((acc, p) => acc + p.total, 0);
    const avgAge = allProfessionals.reduce((acc, p) => acc + (p.avgAge * p.total), 0) / total;
    const totalDepartures = allProfessionals.reduce((acc, p) => acc + p.departures, 0);
    const avgDepartureRate = (totalDepartures / total) * 100;
    const tensionForte = allProfessionals.filter(p => p.tension === 'Forte' || p.tension === 'Très forte').length;
    
    return {
      total,
      avgAge: avgAge.toFixed(1),
      totalDepartures,
      avgDepartureRate: avgDepartureRate.toFixed(1),
      professions: allProfessionals.length,
      tensionForte,
      croissance: '+2.1%'
    };
  }, [allProfessionals]);

  // Données pour les graphiques
  const comparisonData = useMemo(() => {
    return allProfessionals.slice(0, 15).map(p => ({
      name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name,
      total: p.total,
      departures: p.departures,
      fullName: p.name,
      tension: p.tension
    }));
  }, [allProfessionals]);

  const categoryStats = useMemo(() => {
    return [
      { 
        name: 'Santé', 
        value: professionalData.sante.reduce((acc, p) => acc + p.total, 0),
        growth: '+3.2%',
        tension: 'Forte'
      },
      { 
        name: 'Artisanat', 
        value: professionalData.artisans.reduce((acc, p) => acc + p.total, 0),
        growth: '+1.8%',
        tension: 'Très forte'
      },
      { 
        name: 'Commerce', 
        value: professionalData.commercants.reduce((acc, p) => acc + p.total, 0),
        growth: '+0.5%',
        tension: 'Modérée'
      },
      { 
        name: 'Services', 
        value: professionalData.services.reduce((acc, p) => acc + p.total, 0),
        growth: '+4.1%',
        tension: 'Faible'
      }
    ];
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getTensionColor = (tension) => {
    switch (tension) {
      case 'Très forte': return 'bg-red-100 text-red-800 border-red-200';
      case 'Forte': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Modérée': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Faible': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen gradient-bg">
      {/* Header professionnel avec statistiques clés */}
      <header className="relative overflow-hidden bg-gradient-to-br from-orange-500/10 via-orange-400/5 to-orange-300/10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23f97316%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="glass-effect p-6 animate-pulse-glow">
                <Activity className="h-16 w-16 text-orange-500" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-gray-900 mb-4">
              Base Démographique de l'ART
            </h1>
            <p className="text-xl text-orange-600 font-medium mb-6">
              Observatoire National des Effectifs Professionnels en France
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge className="glass-effect border-orange-300 text-orange-700 px-4 py-2 text-sm">
                <Calendar className="h-4 w-4 mr-2" />
                Données 2024
              </Badge>
              <Badge className="glass-effect border-green-300 text-green-700 px-4 py-2 text-sm">
                <TrendingUp className="h-4 w-4 mr-2" />
                Croissance {totalStats.croissance}
              </Badge>
              <Badge className="glass-effect border-blue-300 text-blue-700 px-4 py-2 text-sm">
                <Globe className="h-4 w-4 mr-2" />
                Couverture Nationale
              </Badge>
            </div>
          </div>

          {/* Statistiques globales enrichies */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-6 mb-12">
            <Card className="stat-card col-span-1 xl:col-span-2">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="h-10 w-10 text-orange-500" />
                  <Badge className="bg-orange-100 text-orange-700">Total</Badge>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {(totalStats.total / 1000000).toFixed(1)}M
                </p>
                <p className="text-sm text-gray-600">Professionnels actifs</p>
                <div className="mt-2 flex items-center text-green-600 text-sm">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  +2.1% vs 2023
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Building2 className="h-8 w-8 text-orange-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-1">{totalStats.professions}</p>
                <p className="text-sm text-gray-600">Métiers analysés</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="h-8 w-8 text-orange-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-1">{totalStats.avgAge} ans</p>
                <p className="text-sm text-gray-600">Âge moyen</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingDown className="h-8 w-8 text-orange-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {(totalStats.totalDepartures / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-gray-600">Départs prévus</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Percent className="h-8 w-8 text-orange-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-1">{totalStats.avgDepartureRate}%</p>
                <p className="text-sm text-gray-600">Taux de départ</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <AlertTriangle className="h-8 w-8 text-red-500" />
                </div>
                <p className="text-2xl font-bold text-red-600 mb-1">{totalStats.tensionForte}</p>
                <p className="text-sm text-gray-600">Métiers en tension</p>
              </CardContent>
            </Card>
          </div>

          {/* Indicateurs de performance par secteur */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categoryStats.map((category, index) => (
              <Card key={category.name} className="professional-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                    <Badge className={getTensionColor(category.tension)}>
                      {category.tension}
                    </Badge>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {(category.value / 1000).toFixed(0)}k
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Professionnels</span>
                    <span className="text-sm font-medium text-green-600">{category.growth}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filtres avancés */}
        <Card className="professional-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-orange-500" />
              Recherche et Filtres Avancés
            </CardTitle>
            <CardDescription>
              Explorez les {allProfessionals.length} professions avec des critères personnalisés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Rechercher une profession
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Ex: médecin, boulanger, avocat..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-orange-300 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Secteur d'activité
                </label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="border-orange-300 focus:ring-orange-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    <SelectItem value="sante">🏥 Santé</SelectItem>
                    <SelectItem value="artisans">🔨 Artisanat</SelectItem>
                    <SelectItem value="commercants">🏪 Commerce</SelectItem>
                    <SelectItem value="services">💼 Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Trier par
                </label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="border-orange-300 focus:ring-orange-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total">📊 Effectif Total</SelectItem>
                    <SelectItem value="density">📍 Densité</SelectItem>
                    <SelectItem value="avgAge">📅 Âge Moyen</SelectItem>
                    <SelectItem value="rate">📉 Taux de Départ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Ordre
                </label>
                <Button
                  variant="outline"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className="w-full border-orange-300 hover:bg-orange-50"
                >
                  {sortOrder === 'desc' ? (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Décroissant
                    </>
                  ) : (
                    <>
                      <ChevronUp className="h-4 w-4 mr-2" />
                      Croissant
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tableau professionnel détaillé */}
        <Card className="professional-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Répertoire Professionnel Détaillé</span>
              <Badge className="bg-blue-100 text-blue-700">
                {allProfessionals.length} résultat(s)
              </Badge>
            </CardTitle>
            <CardDescription>
              Analyse complète des effectifs, formations, rémunérations et perspectives par métier
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full professional-table">
                <thead>
                  <tr className="border-b bg-gradient-to-r from-orange-50 to-orange-100">
                    <th className="text-left p-4 font-semibold text-orange-800 min-w-[250px]">
                      Profession & Description
                    </th>
                    <th 
                      className="text-right p-4 font-semibold text-orange-800 cursor-pointer hover:bg-orange-200 min-w-[120px]"
                      onClick={() => handleSort('total')}
                    >
                      Effectifs
                      {sortBy === 'total' && (
                        sortOrder === 'desc' ? <ChevronDown className="inline h-4 w-4 ml-1" /> : <ChevronUp className="inline h-4 w-4 ml-1" />
                      )}
                    </th>
                    <th 
                      className="text-right p-4 font-semibold text-orange-800 cursor-pointer hover:bg-orange-200 min-w-[100px]"
                      onClick={() => handleSort('density')}
                    >
                      Densité
                      {sortBy === 'density' && (
                        sortOrder === 'desc' ? <ChevronDown className="inline h-4 w-4 ml-1" /> : <ChevronUp className="inline h-4 w-4 ml-1" />
                      )}
                    </th>
                    <th className="text-center p-4 font-semibold text-orange-800 min-w-[120px]">
                      Formation
                    </th>
                    <th className="text-center p-4 font-semibold text-orange-800 min-w-[120px]">
                      Rémunération
                    </th>
                    <th className="text-center p-4 font-semibold text-orange-800 min-w-[100px]">
                      Tension
                    </th>
                    <th className="text-center p-4 font-semibold text-orange-800 min-w-[100px]">
                      Évolution
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allProfessionals.map((profession, index) => {
                    const IconComponent = profession.icon;
                    return (
                      <tr 
                        key={profession.name} 
                        className={`border-b transition-all duration-200 hover:bg-orange-50/50 hover:shadow-sm ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              <IconComponent className="h-6 w-6 text-orange-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-gray-900 mb-1">{profession.name}</div>
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{profession.description}</p>
                              <div className="flex flex-wrap gap-1">
                                <Badge className="professional-badge text-xs">
                                  {profession.category}
                                </Badge>
                                {profession.specialites?.slice(0, 2).map((spec, i) => (
                                  <Badge key={i} variant="outline" className="text-xs border-orange-200 text-orange-700">
                                    {spec}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="font-semibold text-gray-900">
                            {profession.total.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {profession.departures.toLocaleString()} départs
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="font-medium text-gray-900">
                            {profession.density.toFixed(1)}
                          </div>
                          <div className="text-xs text-gray-500">
                            /100k hab.
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {profession.formation}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {profession.salaire?.split('-')[0]}k - {profession.salaire?.split('-')[1]}k €
                          </div>
                          <div className="text-xs text-gray-500">brut/an</div>
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={getTensionColor(profession.tension)}>
                            {profession.tension}
                          </Badge>
                        </td>
                        <td className="p-4 text-center">
                          <div className={`text-sm font-medium ${
                            profession.evolution?.startsWith('+') ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {profession.evolution}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Analyses graphiques professionnelles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Analyse détaillée avec onglets */}
          <Card className="professional-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-orange-500" />
                Analyses Sectorielles Approfondies
              </CardTitle>
              <CardDescription>Visualisations interactives des données démographiques</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="evolution" className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-1 bg-orange-100 p-1">
                  <TabsTrigger 
                    value="evolution" 
                    className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs"
                  >
                    📈 Évolution
                  </TabsTrigger>
                  <TabsTrigger 
                    value="projections" 
                    className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs"
                  >
                    🔮 Projections
                  </TabsTrigger>
                  <TabsTrigger 
                    value="regional" 
                    className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs"
                  >
                    🗺️ Régional
                  </TabsTrigger>
                  <TabsTrigger 
                    value="categories" 
                    className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs"
                  >
                    📊 Secteurs
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="evolution" className="mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Évolution des Effectifs 2019-2024</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={evolutionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#FED7AA" />
                      <XAxis dataKey="year" stroke="#374151" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} tickFormatter={(value) => `${value/1000000}M`} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 247, 237, 0.95)',
                          border: '1px solid #F97316',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${(value/1000000).toFixed(1)}M`, 'Effectifs']}
                      />
                      <Line type="monotone" dataKey="sante" stroke="#10B981" strokeWidth={3} name="Santé" />
                      <Line type="monotone" dataKey="artisans" stroke="#F97316" strokeWidth={3} name="Artisanat" />
                      <Line type="monotone" dataKey="commercants" stroke="#3B82F6" strokeWidth={3} name="Commerce" />
                      <Line type="monotone" dataKey="services" stroke="#8B5CF6" strokeWidth={3} name="Services" />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700">
                      <strong>Tendances :</strong> Croissance soutenue du secteur santé (+8%) et services (+32%) sur 5 ans. 
                      Stabilisation de l'artisanat et du commerce après la crise COVID-19.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="projections" className="mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Projections Besoins vs Effectifs 2024-2029</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={projectionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#FED7AA" />
                      <XAxis dataKey="year" stroke="#374151" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} tickFormatter={(value) => `${value/1000000}M`} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 247, 237, 0.95)',
                          border: '1px solid #F97316',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${(value/1000000).toFixed(1)}M`, '']}
                      />
                      <Area type="monotone" dataKey="besoin" stackId="1" stroke="#EF4444" fill="#FEE2E2" name="Besoins estimés" />
                      <Line type="monotone" dataKey="effectif" stroke="#F97316" strokeWidth={3} name="Effectifs disponibles" />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-gray-700">
                      <strong>⚠️ Alerte :</strong> Déficit croissant prévu de 270k professionnels d'ici 2029. 
                      Actions urgentes nécessaires en formation et attractivité des métiers.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="regional" className="mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Densité Professionnelle par Région</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={regionalData} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" stroke="#FED7AA" />
                      <XAxis type="number" stroke="#6B7280" fontSize={12} />
                      <YAxis 
                        type="category" 
                        dataKey="region" 
                        stroke="#374151" 
                        fontSize={10}
                        width={120}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 247, 237, 0.95)',
                          border: '1px solid #F97316',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name, props) => [
                          `${value} prof./100k hab.`,
                          `Chômage: ${props.payload.chomage}%`
                        ]}
                      />
                      <Bar dataKey="density" fill="#F97316" radius={[4, 4, 4, 4]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700">
                      <strong>Analyse régionale :</strong> Forte concentration en Île-de-France (580/100k). 
                      Disparités importantes avec les régions rurales nécessitant des politiques d'aménagement.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="categories" className="mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Répartition par Secteur d'Activité</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={categoryStats}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryStats.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 247, 237, 0.95)',
                          border: '1px solid #F97316',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${(value/1000).toFixed(0)}k professionnels`, '']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {categoryStats.map((cat, index) => (
                      <div key={cat.name} className="p-3 bg-white rounded-lg border border-orange-200">
                        <div className="flex items-center gap-2 mb-1">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: COLORS[index] }}
                          ></div>
                          <span className="font-medium text-sm">{cat.name}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          Croissance: <span className="font-medium text-green-600">{cat.growth}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Comparaison des professions */}
          <Card className="professional-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-orange-500" />
                Comparaison des Effectifs par Profession
              </CardTitle>
              <CardDescription>Top 15 des professions par nombre d'actifs</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#FED7AA" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#374151" 
                    fontSize={10}
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis 
                    stroke="#6B7280" 
                    fontSize={12}
                    tickFormatter={(value) => `${value/1000}k`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(255, 247, 237, 0.95)',
                      border: '1px solid #F97316',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name, props) => [
                      `${value.toLocaleString()} professionnels`,
                      props.payload.fullName
                    ]}
                  />
                  <Bar dataKey="total" fill="#F97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Légende des tensions */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Légende des niveaux de tension</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-200 rounded"></div>
                    <span className="text-sm">Très forte</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-200 rounded"></div>
                    <span className="text-sm">Forte</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-200 rounded"></div>
                    <span className="text-sm">Modérée</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-200 rounded"></div>
                    <span className="text-sm">Faible</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insights et recommandations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="professional-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Alertes Critiques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Médecins spécialistes</p>
                  <p className="text-xs text-red-600">33% de départs prévus - Déserts médicaux</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Bouchers-charcutiers</p>
                  <p className="text-xs text-red-600">30% de départs - Métier physique</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Restaurateurs</p>
                  <p className="text-xs text-red-600">Difficultés de recrutement généralisées</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="professional-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Secteurs Porteurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">Psychologues</p>
                  <p className="text-xs text-green-600">+6.8% croissance - Santé mentale</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">Orthophonistes</p>
                  <p className="text-xs text-green-600">+5.2% croissance - Forte demande</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">Coachs professionnels</p>
                  <p className="text-xs text-green-600">+8.5% croissance - Secteur émergent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="professional-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600">
                <Info className="h-5 w-5" />
                Recommandations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800">Formation</p>
                  <p className="text-xs text-blue-600">Renforcer l'apprentissage dans l'artisanat</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800">Attractivité</p>
                  <p className="text-xs text-blue-600">Améliorer les conditions de travail</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800">Territoires</p>
                  <p className="text-xs text-blue-600">Incitations installation zones rurales</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer professionnel */}
      <footer className="bg-gradient-to-r from-orange-50 to-orange-100 border-t border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Activity className="h-6 w-6 text-orange-500" />
                <span className="font-bold text-gray-900">ART Database</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Observatoire national des effectifs professionnels en France. 
                Données officielles et analyses prospectives.
              </p>
              <div className="flex gap-2">
                <Badge className="bg-orange-100 text-orange-700">Données 2024</Badge>
                <Badge className="bg-green-100 text-green-700">Certifié</Badge>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Secteurs</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>🏥 Santé ({professionalData.sante.length} métiers)</li>
                <li>🔨 Artisanat ({professionalData.artisans.length} métiers)</li>
                <li>🏪 Commerce ({professionalData.commercants.length} métiers)</li>
                <li>💼 Services ({professionalData.services.length} métiers)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Analyses</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>📊 Effectifs par région</li>
                <li>📈 Évolutions temporelles</li>
                <li>🔮 Projections 2029</li>
                <li>⚠️ Métiers en tension</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Méthodologie</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>📋 Sources officielles</li>
                <li>🔄 Mise à jour continue</li>
                <li>📐 Méthodes statistiques</li>
                <li>✅ Validation experte</li>
              </ul>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-orange-200">
            <p className="text-sm text-gray-600">
              © 2024 Base Démographique de l'ART - Observatoire National des Effectifs Professionnels
            </p>
            <a 
              href="https://app.emergent.sh/?utm_source=emergent-badge" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 glass-effect px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
            >
              <img 
                alt="Emergent" 
                className="w-6 h-6 rounded" 
                src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              />
              <span className="text-sm font-medium">Made with Emergent</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}